import { ExternalLink, FileLock2, Layers, RefreshCw } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import { queryKeys } from "@/api/hooks";
import { cn } from "@/lib/cn";
import {
  classifyRunReadFailure,
  usePreviewRunEventQuery,
  usePromptProvenanceQuery,
  usePromptSourceQuery,
} from "@/api/previewInsights";
import { QueryPendingState } from "@/api/QueryPendingState";
import { CONTENT_UNAVAILABLE_LABELS } from "@/lib/previewInsights";
import {
  VISIBILITY_LABELS,
  describeOmission,
  type ContentBody,
  type PromptCall,
} from "@/lib/previewPromptCall";
import {
  ORIGIN_AUTHORSHIP,
  ORIGIN_LABELS,
  ORIGIN_TONES,
  SECTION_LABELS,
  compositionAgreement,
  describeAgreement,
  describeFrozenBody,
  describeFrozenSource,
  describeLayers,
  describeSource,
  hasCandidateAuthoredLayer,
  layerChips,
  readFrozenSource,
  readPromptProvenance,
  readPromptProvenanceDetail,
  sourceHref,
  type FrozenSource,
  type PreviewRunPromptProvenanceResponse,
  type PromptLayer,
  type PromptProvenance,
} from "@/lib/previewPromptProvenance";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";
import { Notice } from "@/ui/Notice";
import { PromptBody } from "@/ui/preview/ExactPromptCall";
import { EmptyState, ErrorState } from "@/ui/states";

import { Identifier, PreviewReadFailureState } from "@/routes/previewRunParts";

/**
 * Preview Insights — the prompt's ordered provenance layers (#1101, approved
 * design §10.3).
 *
 * Beside the exact prompt, the account of how it was assembled: the named
 * layers in the order the runtime composed them, who authored each, and — for
 * a layer that came from a clinical document — a link to the Editor section
 * where that document is authored.
 *
 * ## Nothing is composed, inferred or re-attributed here
 *
 * The order is the server's. The attribution is liv-brain#419's, which is
 * itself checked at source: a composition whose segments do not rejoin into
 * the exact prompt byte for byte is DISCARDED by the producer rather than
 * published, so the layers a clinician sees are the layers that actually
 * reconstruct what Liv sent. This panel never joins them, never hashes them
 * and never decides what a layer is. The prompt shown on this page is the
 * call's own `systemPrompt` body, above; what this panel adds is the account
 * of where it came from, plus the producer's own digest tying the two — which
 * it compares and never computes.
 *
 * ## What "authored" means is said outright
 *
 * Every layer states its author in a sentence, not by colour: inherited base
 * content says it was inherited and not authored here; runtime-composed text
 * says nobody authored it as it stands; the synthetic patient's context says
 * it is the preview patient's material. Only `candidate_authored` says the
 * candidate under test supplies it. That is the Definition of Done's "never
 * claims authored ownership for inherited or runtime content", held as a
 * property of the words rather than of a badge.
 *
 * ## A source link is built from a slug and this Project
 *
 * `sourceHref` composes `/projects/{thisProject}/config/{slug}` from the
 * section the Editor Service resolved — one of seven closed values — and the
 * Project id this run page is mounted under. The producer's coordinate is not
 * an input to any address: a link cannot leave this Project, cannot select
 * another candidate, and cannot become a filesystem path or an internal URL.
 * The coordinate is still SHOWN, as inert monospace text, because a clinician
 * whose document has no screen in this build is owed its name.
 *
 * ## The candidate is immutable, and the destination is not the candidate
 *
 * A preview run is bound to a frozen candidate. The clinical-configuration
 * section a link opens is the PROJECT's live configuration — where the
 * document is edited now — and this panel says so rather than implying the
 * link opens the bytes the run used. The run's own immutable copy of those
 * bytes is what the layer text below shows, loaded from the recorded preview
 * target; nothing a clinician does on the destination screen can change it.
 *
 * ## Bodies are references until a clinician asks
 *
 * The layer list costs no prompt text: every layer arrives with a digest, a
 * size, a visibility class and any omission reason. Pressing the control reads
 * the provenance event through `GET …/events/{eventRef}` — the one read that
 * carries a body and the one the Editor Service audits as full prompt detail
 * viewed — and the bytes are then rendered by #1099's own inert-text renderer.
 */
export function PromptProvenanceLayers({
  call,
  projectId,
  previewRunRef,
}: {
  call: PromptCall;
  projectId: string;
  previewRunRef: string;
}) {
  const headingId = useId();
  const query = usePromptProvenanceQuery(
    projectId,
    previewRunRef,
    call.eventRef,
    { enabled: true },
  );

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <h3 id={headingId} className="text-sm font-semibold text-foreground">
        Prompt layers
      </h3>
      {query.isPending || !query.isFetchedAfterMount ? (
        <QueryPendingState
          isPaused={query.isPaused}
          failureReason={query.failureReason}
          label="Reading how this prompt was composed"
          errorTitle="This prompt's layers could not be read"
          rows={3}
          queryKey={queryKeys.previewRunPromptProvenance(
            projectId,
            previewRunRef,
            call.eventRef,
          )}
        />
      ) : query.isError ? (
        <PreviewReadFailureState
          subject="prompt_call"
          failure={classifyRunReadFailure(query.error)}
          error={query.error}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <LoadedProvenance
          reading={readPromptProvenance(query.data)}
          run={query.data.run}
          call={call}
          projectId={projectId}
          previewRunRef={previewRunRef}
        />
      )}
    </section>
  );
}

function LoadedProvenance({
  reading,
  run,
  call,
  projectId,
  previewRunRef,
}: {
  reading: ReturnType<typeof readPromptProvenance>;
  run: PreviewRunPromptProvenanceResponse["run"];
  call: PromptCall;
  projectId: string;
  previewRunRef: string;
}) {
  switch (reading.kind) {
    case "content_unavailable": {
      const reason = run.contentUnavailableReason ?? null;
      return (
        <EmptyState
          title="This run's trace has no readable content"
          description={
            reason !== null
              ? `The Editor Service reports that ${CONTENT_UNAVAILABLE_LABELS[reason]}. How this prompt was composed cannot be shown.`
              : "The Editor Service reports no readable content for this run and names no reason. How this prompt was composed cannot be shown."
          }
        />
      );
    }
    case "not_recorded":
      return (
        <EmptyState
          title="No layers were recorded for this prompt"
          description="This run holds the call and no account of how its prompt was composed. Liv records that account separately from the prompt, and it is absent here — either this kind of call is not instrumented for it in the serving runtime, or the account could not be published for this call. The exact prompt above is unaffected: it is what was sent."
        />
      );
    case "unreadable":
      return (
        <ErrorState
          title="This prompt's layers are not ones this build can read"
          description={`The Editor Service relayed an account this build cannot place (at ${reading.at}). Nothing is shown rather than part of a composition: a prompt explained by some of its layers reads as a prompt explained by all of them.`}
        />
      );
    case "readable":
      return (
        <ProvenancePanel
          // KEYED BY THE EVENT the consent belongs to (#1101 review, finding
          // 5). `requested` is a clinician's decision to spend the one
          // body-bearing, audited read on THIS composition; keeping it across
          // a change of event would fire that read for a composition nobody
          // asked to see. The selection is keyed by its caller today, so this
          // is defence in depth — and it makes the property local to the thing
          // it is about rather than dependent on a caller remembering a key.
          key={reading.provenance.eventRef}
          provenance={reading.provenance}
          call={call}
          projectId={projectId}
          previewRunRef={previewRunRef}
        />
      );
  }
}

function ProvenancePanel({
  provenance,
  call,
  projectId,
  previewRunRef,
}: {
  provenance: PromptProvenance;
  call: PromptCall;
  projectId: string;
  previewRunRef: string;
}) {
  const agreement = compositionAgreement(provenance, call);
  if (agreement.kind === "disagrees" || agreement.kind === "other_call") {
    // TERMINAL, not a warning above the layers (#1101 review, finding 4).
    // These layers are not this prompt's: the producer either tied them to
    // another composition or named another call outright. Rendering them under
    // "This prompt was composed from…", with source links into clinical
    // documents and a control that loads their bytes, would present another
    // prompt's account as this one's — and contradict the warning sitting on
    // top of it. Nothing about them is shown, and nothing is loaded.
    return <ForeignComposition agreement={agreement} provenance={provenance} />;
  }
  return (
    <AttachedComposition
      provenance={provenance}
      agreement={agreement}
      projectId={projectId}
      previewRunRef={previewRunRef}
      callEventRef={call.eventRef}
    />
  );
}

/**
 * The layers of a composition the producer did not tie to the prompt on this
 * page. Its identity and the disagreement, and none of its content.
 */
function ForeignComposition({
  agreement,
  provenance,
}: {
  agreement: ReturnType<typeof compositionAgreement>;
  provenance: PromptProvenance;
}) {
  return (
    <div className="space-y-2">
      <Notice tone="destructive">{describeAgreement(agreement)}</Notice>
      <p className="text-sm text-muted-foreground">
        No layer is shown and no layer text is loaded: an account of how a
        different prompt was composed is not an account of the prompt above, and
        presenting it as one would be a false explanation rather than a missing
        one. The exact prompt above is unaffected — it is what was sent.
      </p>
      <p className="text-xs text-muted-foreground">
        <span className="sr-only">Provenance event reference </span>
        <Identifier value={provenance.eventRef} />
      </p>
    </div>
  );
}

/**
 * The address member naming the layer whose frozen source is open.
 *
 * An ORDINAL, scoped by the `?event=` the run page already carries: the
 * address names a Project, a run, a call and a position, and nothing else. No
 * coordinate, no candidate and no path is in it or could be — which is what
 * makes "a source link cannot name another candidate or spell a filesystem
 * path" a property of the address's vocabulary rather than of this component.
 */
export const RUN_SOURCE_PARAM = "source";

/** The layer position an address names, or null. Bounded, digits only. */
export function readSourceIndex(
  params: URLSearchParams,
  layers: number,
): number | null {
  const raw = params.get(RUN_SOURCE_PARAM);
  if (raw === null || !/^\d{1,3}$/.test(raw)) {
    return null;
  }
  const index = Number(raw);
  // A position this composition does not hold is not a position: a pasted
  // address, or one kept across a change of call. Dropped rather than sent.
  return index < layers ? index : null;
}

function AttachedComposition({
  provenance,
  agreement,
  projectId,
  previewRunRef,
  callEventRef,
}: {
  provenance: PromptProvenance;
  agreement: ReturnType<typeof compositionAgreement>;
  projectId: string;
  previewRunRef: string;
  /**
   * The PROMPT CALL's own ref — what the source address names, and what this
   * panel is about. Deliberately not the producer's `promptEventRef` echo:
   * the reader is tolerant about that member, so a response omitting it would
   * disable the source query and leave the view loading forever. By the time
   * this component renders, that echo has already been checked against this
   * ref (a disagreement is a foreign composition and never reaches here).
   */
  callEventRef: string;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const addressedIndex = readSourceIndex(
    searchParams,
    provenance.layers.length,
  );
  const addressedLayer =
    addressedIndex === null ? null : provenance.layers[addressedIndex]!;
  const openIndex =
    addressedLayer !== null &&
    provenance.visibility !== "restricted_technical" &&
    addressedLayer.content.visibility !== "restricted_technical"
      ? addressedIndex
      : null;
  const openSource = (index: number | null) =>
    setSearchParams(
      (current: URLSearchParams) => {
        const next = new URLSearchParams(current);
        if (index === null) {
          next.delete(RUN_SOURCE_PARAM);
        } else {
          next.set(RUN_SOURCE_PARAM, String(index));
        }
        return next;
      },
      { replace: true },
    );
  const [requested, setRequested] = useState(false);
  const detail = usePreviewRunEventQuery(
    projectId,
    previewRunRef,
    provenance.eventRef,
    { enabled: requested },
  );
  const chips = layerChips(provenance.layers);
  const listHeadingId = useId();
  const listRef = useRef<HTMLHeadingElement>(null);
  // FOCUS FOLLOWS THE ANSWER a keyboard user asked for: the control is
  // replaced by the loaded text, so without this focus would fall to `body`
  // and a screen reader would announce nothing. Only after a press.
  const focusOnArrival = useRef(false);
  useEffect(() => {
    if (detail.isSuccess && focusOnArrival.current) {
      focusOnArrival.current = false;
      listRef.current?.focus();
    }
  }, [detail.isSuccess]);

  const bodies =
    detail.isSuccess && !detail.isPending
      ? readPromptProvenanceDetail(detail.data, {
          eventRef: provenance.eventRef,
          visibility: provenance.visibility,
          layers: provenance.layers.map((layer) => layer.identity),
        })
      : null;

  return (
    <div className="space-y-4">
      {provenance.visibility === "restricted_technical" ? (
        <Notice tone="warning">
          Restricted technical content. Every layer of this composition was
          withheld at source by policy; each layer&apos;s safe label, size,
          digest and outcome are shown. Nothing is hidden by this page, and
          nothing withheld is reconstructed.
        </Notice>
      ) : null}

      <p role="status" className="text-sm text-muted-foreground">
        {describeLayers(provenance.layers)}
      </p>

      {/*
        THE CHIPS, in the order the prompt introduces each origin. A list
        rather than loose spans so a screen reader announces "5 items" and can
        step through them, and each item's accessible name is a whole sentence
        rather than a label glued to a number.
      */}
      <ul
        className="flex flex-wrap items-center gap-2"
        aria-label="Provenance layers, in composition order"
      >
        {chips.map((chip) => (
          <li key={chip.origin}>
            <Badge
              tone={ORIGIN_TONES[chip.origin]}
              data-provenance-chip={chip.origin}
            >
              <span aria-hidden="true">
                {ORIGIN_LABELS[chip.origin]}{" "}
                <span dir="ltr">({chip.count})</span>
              </span>
              <span className="sr-only">
                {`${ORIGIN_LABELS[chip.origin]}: ${chip.count} of this prompt's ${provenance.layers.length} layers`}
              </span>
            </Badge>
          </li>
        ))}
      </ul>

      <p className="text-xs text-muted-foreground">
        {describeAgreement(agreement)}
      </p>

      {hasCandidateAuthoredLayer(provenance.layers) ? (
        <p className="text-xs text-muted-foreground">
          A source link opens this Project&apos;s clinical configuration, where
          the document is edited now. The prompt above came from the immutable
          candidate this run was dispatched with, so what you open may have
          changed since; nothing you do there alters this run.
        </p>
      ) : null}

      <section aria-labelledby={listHeadingId} className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h4
            id={listHeadingId}
            ref={listRef}
            tabIndex={-1}
            className="text-sm font-medium text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            Layers, in composition order
          </h4>
          {provenance.visibility ===
          "restricted_technical" ? null : !requested ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                focusOnArrival.current = true;
                setRequested(true);
              }}
            >
              <Layers aria-hidden="true" className="h-4 w-4" />
              Load each layer&apos;s text
            </Button>
          ) : detail.isSuccess ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void detail.refetch()}
              disabled={detail.isFetching}
            >
              <RefreshCw aria-hidden="true" className="h-4 w-4" />
              Reload
            </Button>
          ) : null}
        </div>

        {provenance.visibility === "restricted_technical" ? null : requested ? (
          <LayerTextState detail={detail} bodies={bodies} />
        ) : (
          <p className="text-sm text-muted-foreground">
            Each layer below carries its size, digest and visibility class,
            never its text. Load the text to see which bytes each layer
            contributed; everything loaded is rendered as text, and nothing in
            it is interpreted as markup, a link or a control.
          </p>
        )}

        <ol className="space-y-4">
          {provenance.layers.map((layer) => (
            <li key={layer.position}>
              <LayerRow
                layer={layer}
                total={provenance.layers.length}
                projectId={projectId}
                onOpenSource={
                  provenance.visibility === "restricted_technical" ||
                  layer.content.visibility === "restricted_technical"
                    ? null
                    : () => openSource(layer.position)
                }
                body={
                  bodies !== null && bodies.kind === "readable"
                    ? (bodies.bodies[layer.position] ?? null)
                    : null
                }
              />
            </li>
          ))}
        </ol>
      </section>

      {openIndex !== null ? (
        <FrozenSourceDialog
          key={`${provenance.eventRef}/${openIndex}`}
          projectId={projectId}
          previewRunRef={previewRunRef}
          eventRef={callEventRef}
          provenance={provenance}
          layer={provenance.layers[openIndex]!}
          onClose={() => openSource(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * ONE layer's authoring document, as THIS RUN's immutable candidate froze it
 * (#1101 review, finding 1).
 *
 * ## Why this exists beside the section link
 *
 * A preview run is bound to a frozen candidate, and the prompt it sent was
 * composed from THAT candidate's documents. The clinical-configuration section
 * a layer also links to is where the document is authored NOW — after an edit,
 * different bytes — so offering only that link would show a clinician content
 * the run never used and invite them to read it as the content it did. This
 * view is the run's own copy: hash-verified against the row that committed it,
 * and immutable because a frozen candidate is.
 *
 * ## Read-only by construction, not by a flag
 *
 * There is no control here that writes, and there is no route in the Editor
 * Service that writes a frozen candidate. The dialog states which testing
 * version the bytes belong to, so what a clinician is reading is never
 * ambiguous with the draft they may edit next door.
 *
 * ## The address names a position, never a document
 *
 * The browser asks by the layer's ordinal; the coordinate, the archive path
 * and the candidate are all resolved server-side off the run's immutable
 * correlation row. The coordinate and path below are inert text — shown so a
 * clinician can recognise the document, never interpolated into a request or
 * a link.
 */
function FrozenSourceDialog({
  projectId,
  previewRunRef,
  eventRef,
  provenance,
  layer,
  onClose,
}: {
  projectId: string;
  previewRunRef: string;
  eventRef: string;
  provenance: PromptProvenance;
  layer: PromptLayer;
  onClose: () => void;
}) {
  const query = usePromptSourceQuery(
    projectId,
    previewRunRef,
    eventRef,
    layer.position,
  );
  const reading = query.isSuccess
    ? readFrozenSource(query.data, { provenance, index: layer.position })
    : null;
  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <DialogContent
        title={layer.label ?? `Layer ${layer.position + 1}`}
        description="The clinical document behind this layer, exactly as the immutable candidate this run was dispatched with holds it. Read-only: a frozen candidate cannot be edited, here or anywhere."
        className="w-[min(48rem,calc(100vw-2rem))]"
        footer={
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        }
      >
        {query.isPending || !query.isFetchedAfterMount ? (
          <p className="text-sm text-muted-foreground">
            Reading the frozen document…
          </p>
        ) : query.isError ? (
          <PreviewReadFailureState
            subject="prompt_call"
            failure={classifyRunReadFailure(query.error)}
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : reading === null ? null : reading.kind === "content_unavailable" ? (
          <Notice tone="warning">
            The Editor Service reports no readable content for this run, so its
            candidate&apos;s frozen document cannot be reached from here.
          </Notice>
        ) : reading.kind === "unreadable" ? (
          <Notice tone="destructive">
            {`The Editor Service relayed a source this build cannot place (at ${reading.at}). Nothing is shown rather than part of a document.`}
          </Notice>
        ) : reading.kind === "mismatch" ? (
          <Notice tone="destructive">
            The Editor Service answered with a different composition or layer
            than the one already on screen. No source bytes are shown rather
            than one layer&apos;s document under another layer&apos;s identity.
          </Notice>
        ) : (
          <FrozenSourceBody source={reading.source} origin={layer.origin} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function FrozenSourceBody({
  source,
  origin,
}: {
  source: FrozenSource;
  origin: PromptLayer["origin"];
}) {
  const absence = describeFrozenBody(source);
  return (
    <div className="space-y-3">
      <p className="text-sm text-foreground">{describeFrozenSource(source)}</p>
      <p className="text-xs text-muted-foreground">
        {ORIGIN_AUTHORSHIP[origin]}
      </p>
      <dl className="grid gap-x-6 gap-y-2 text-xs sm:grid-cols-2">
        {source.coordinate !== null ? (
          <div>
            <dt className="text-muted-foreground">Clinical coordinate</dt>
            <dd>
              <Identifier value={source.coordinate} />
            </dd>
          </div>
        ) : null}
        {source.path !== null ? (
          <div>
            <dt className="text-muted-foreground">
              Path in the frozen candidate
            </dt>
            <dd>
              <Identifier value={source.path} />
            </dd>
          </div>
        ) : null}
        {source.byteLength !== null ? (
          <div>
            <dt className="text-muted-foreground">Frozen size</dt>
            <dd dir="ltr">{source.byteLength} bytes</dd>
          </div>
        ) : null}
        {source.sha256 !== null ? (
          <div>
            <dt className="text-muted-foreground">
              Digest of the frozen bytes
            </dt>
            <dd>
              <Identifier value={source.sha256} />
            </dd>
          </div>
        ) : null}
      </dl>
      {source.text !== null ? (
        <pre
          data-slot="frozen-source"
          dir="auto"
          className={cn(
            "max-h-[24rem] max-w-full overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted px-3 py-2 text-start font-mono text-xs leading-5 text-foreground",
            "[overflow-wrap:anywhere] [unicode-bidi:plaintext]",
          )}
        >
          {source.text}
        </pre>
      ) : absence !== null ? (
        <p className="text-sm text-muted-foreground">{absence}</p>
      ) : null}
    </div>
  );
}

/** What the on-demand text read is doing, or why it cannot be shown. */
function LayerTextState({
  detail,
  bodies,
}: {
  detail: ReturnType<typeof usePreviewRunEventQuery>;
  bodies: ReturnType<typeof readPromptProvenanceDetail> | null;
}) {
  if (detail.isPending || !detail.isFetchedAfterMount) {
    return (
      <p className="text-sm text-muted-foreground">
        Loading each layer&apos;s text…
      </p>
    );
  }
  if (detail.isError) {
    return (
      <PreviewReadFailureState
        subject="prompt_call"
        failure={classifyRunReadFailure(detail.error)}
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
    );
  }
  if (bodies === null) {
    return null;
  }
  switch (bodies.kind) {
    case "content_unavailable":
      return (
        <Notice tone="warning">
          The Editor Service reports no readable content for this run, so no
          layer text can be shown. The layers themselves are unaffected.
        </Notice>
      );
    case "mismatch":
      return (
        <Notice tone="destructive">
          The Editor Service answered with a different composition than the one
          listed here. No layer text is shown rather than one layer&apos;s bytes
          under another layer&apos;s attribution.
        </Notice>
      );
    case "unreadable":
      return (
        <Notice tone="destructive">
          {`The loaded layer text is not something this build can read (at ${bodies.at}). Nothing is shown rather than part of a composition.`}
        </Notice>
      );
    case "readable":
      return (
        <p className="text-xs text-muted-foreground">
          Loaded on request from the recorded preview target, exactly as this
          run recorded it. Rendered as text only.
        </p>
      );
  }
}

/**
 * ONE layer: where it sits, who authored it, which document it came from, and
 * what the producer declared about its bytes.
 */
function LayerRow({
  layer,
  total,
  projectId,
  body,
  onOpenSource,
}: {
  layer: PromptLayer;
  total: number;
  projectId: string;
  body: ContentBody | null;
  onOpenSource: (() => void) | null;
}) {
  return (
    <div
      data-slot="prompt-layer"
      data-origin={layer.origin}
      className="space-y-2 rounded-md border border-border p-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone="outline">
          <span aria-hidden="true" dir="ltr">
            {layer.position + 1}
          </span>
          <span className="sr-only">{`Layer ${layer.position + 1} of ${total}`}</span>
        </Badge>
        <Badge tone={ORIGIN_TONES[layer.origin]}>
          {ORIGIN_LABELS[layer.origin]}
        </Badge>
      </div>

      {/* The producer's own clinician-facing label: clinical text, so it chooses its own direction. */}
      <p dir="auto" className="text-sm font-medium text-foreground">
        {layer.label ?? "This layer carries no label."}
      </p>

      <p className="text-xs text-muted-foreground">
        {ORIGIN_AUTHORSHIP[layer.origin]}
      </p>

      {onOpenSource !== null ? (
        <LayerSourceLine layer={layer} projectId={projectId} />
      ) : null}

      {/*
        THE RUN'S OWN COPY, offered for every non-restricted layer including
        one whose document no section serves and one the candidate did not
        supply. Restricted technical layers expose neither a source route nor
        a live-configuration link in this browser.
      */}
      {onOpenSource !== null ? (
        <div>
          <Button variant="outline" size="sm" onClick={onOpenSource}>
            <FileLock2 aria-hidden="true" className="h-4 w-4" />
            <span aria-hidden="true">Open the frozen source</span>
            <span className="sr-only">
              {`Open the frozen source of layer ${layer.position + 1} of ${total}, read-only, as the candidate this run used holds it`}
            </span>
          </Button>
        </div>
      ) : null}

      <LayerFacts body={layer.content} />

      {body !== null ? <PromptBody body={body} /> : null}
    </div>
  );
}

/** Where this layer's document is opened, or why it is not offered. */
function LayerSourceLine({
  layer,
  projectId,
}: {
  layer: PromptLayer;
  projectId: string;
}) {
  const { source } = layer;
  // Two states carry no coordinate to show, and they are two different facts:
  // the layer names no document, and the service named no destination for it.
  // `describeSource` keeps their sentences apart.
  if (source.kind === "absent" || source.kind === "unresolved") {
    return (
      <p className="text-xs text-muted-foreground">
        {describeSource(source, layer.origin)}
      </p>
    );
  }
  return (
    <p className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs text-muted-foreground">
      <span>{describeSource(source, layer.origin)}</span>
      {/* The coordinate is INERT TEXT in every state, including the linked
          one: it names the clinical document, and the link is built from the
          section slug beside it rather than from this string. */}
      <span className="inline-flex items-baseline gap-1">
        <span className="sr-only">Clinical source coordinate</span>
        <Identifier value={source.coordinate} />
      </span>
      {source.kind === "section" ? (
        <Link
          to={sourceHref(projectId, source.section)}
          className="inline-flex items-center gap-1 font-medium text-primary underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          <ExternalLink aria-hidden="true" className="h-3.5 w-3.5" />
          <span aria-hidden="true">Open {SECTION_LABELS[source.section]}</span>
          <span className="sr-only">
            {`Open ${SECTION_LABELS[source.section]} in this project, where ${source.coordinate} is edited`}
          </span>
        </Link>
      ) : null}
    </p>
  );
}

/** What the producer declared about this layer's bytes — size, digest, class, omission. */
function LayerFacts({ body }: { body: ContentBody }) {
  return (
    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
      {body.state === "omitted" ? <span>{describeOmission(body)}</span> : null}
      {body.byteSize !== null ? (
        <span>
          <span dir="ltr">{body.byteSize}</span> bytes
        </span>
      ) : null}
      {body.contentHash !== null ? (
        <span className="inline-flex items-baseline gap-1">
          Digest <Identifier value={body.contentHash} />
        </span>
      ) : null}
      {body.visibility !== null ? (
        <span>{VISIBILITY_LABELS[body.visibility]}</span>
      ) : null}
    </p>
  );
}
